import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Plot from 'react-plotly.js';
import { NavigationContext } from '../context/NavigationContext';
import { ChatContext } from '../context/ChatContext';
import useWindowResize from '../hooks/useWindowResize';
import API_URL from '../config';

const NewAnnotation = () => {
  const { recordingId, epochId } = useParams();
  const navigate = useNavigate();
  const { updateLevel, goToLevel } = useContext(NavigationContext);
  const { setChatDictionary } = useContext(ChatContext);
  const windowSize = useWindowResize();
  
  // State for the form
  const [annotationType, setAnnotationType] = useState('spindle');
  const [channel, setChannel] = useState('');
  const [start, setStart] = useState(null);
  const [end, setEnd] = useState(null);
  const [selectedRange, setSelectedRange] = useState(null);
  
  // State for the data
  const [epochData, setEpochData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Sampling frequency
  const sf = 50;
  
  useEffect(() => {
    updateLevel('new-annotation', { 
      label: 'New Annotation', 
      route: `/analysis/${recordingId}/epoch/${epochId}/annotation/new`
    });
  }, [epochId, recordingId, updateLevel]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${API_URL}/api/epochs/${epochId}/`);
        if (!response.ok) {
          throw new Error('Failed to fetch data');
        }
        const data = await response.json();
        setEpochData(data);
      } catch (err) {
        console.error("Error fetching data:", err);
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [epochId]);

  // Update chat context
  useEffect(() => {
    if (epochData) {
      const chatDictionary = {
        level: 'new-annotation',
        action: 'new',
        epochData: epochData
      };
      setChatDictionary(chatDictionary);
    }
  }, [epochData, setChatDictionary]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!start || !end) {
      alert('Please select a range on the signal plot');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/epochs/${epochId}/annotations/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          annotation_type: annotationType,
          start: start,
          end: end,
          channel: channel || null
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to create annotation');
      }

      // Navigate back to the epoch view
      goToLevel('epoch');
    } catch (err) {
      console.error('Error creating annotation:', err);
      alert('Failed to create annotation: ' + err.message);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-red-500 p-4">
        Error loading epoch data: {error.message}
      </div>
    );
  }

  // Get available channels from the epoch data
  const channels = epochData?.signals?.map(s => s.signal_name) || [];

  // Get the signal data for the selected channel
  const signal = channel
    ? epochData?.signals?.find(s => s.signal_name === channel)
    : epochData?.signals?.[0];

  let signalData = [];
  if (signal) {
    try {
      signalData = typeof signal.signal_data === 'string'
        ? JSON.parse(signal.signal_data)
        : signal.signal_data;
    } catch (err) {
      console.error("Error parsing signal data", err);
    }
  }

  return (
    <div className="p-6 bg-white rounded shadow">
      <h1 className="text-2xl font-bold mb-4">New Annotation</h1>
      
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Annotation Type */}
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Annotation Type
          </label>
          <select
            value={annotationType}
            onChange={(e) => setAnnotationType(e.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          >
            <option value="spindle">Spindle</option>
            <option value="k-complex">K-complex</option>
            <option value="slow-wave">Slow Wave</option>
            <option value="rem">REM</option>
            <option value="alpha">Alpha</option>
            <option value="movement">Movement</option>
          </select>
        </div>

        {/* Channel Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Channel (optional)
          </label>
          <select
            value={channel}
            onChange={(e) => setChannel(e.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          >
            <option value="">Global (no specific channel)</option>
            {channels.map((ch) => (
              <option key={ch} value={ch}>{ch}</option>
            ))}
          </select>
        </div>

        {/* Signal Plot */}
        <div className="border border-gray-300 bg-gray-50 p-4">
          <Plot
            data={[
              {
                x: signalData.map((_, i) => i),
                y: signalData,
                type: 'scatter',
                mode: 'lines',
                line: { color: 'rgb(73,68,68)', width: 0.5 },
              }
            ]}
            layout={{
              height: 300,
              margin: { l: 50, r: 20, t: 20, b: 40 },
              xaxis: {
                title: 'Sample',
                range: [0, signalData.length]
              },
              yaxis: { title: 'Amplitude' },
              shapes: selectedRange ? [{
                type: 'rect',
                x0: selectedRange.start,
                x1: selectedRange.end,
                y0: Math.min(...signalData),
                y1: Math.max(...signalData),
                fillcolor: 'rgba(66,153,225,0.3)',
                line: { width: 1, color: 'rgb(66,153,225)' }
              }] : []
            }}
            config={{
              displayModeBar: false,
              responsive: true,
              dragmode: 'select'
            }}
            style={{ width: '100%', height: '100%' }}
            useResizeHandler={true}
            key={`new-annotation-${windowSize.width}-${windowSize.height}`}
            onSelected={(eventData) => {
              if (eventData?.range?.x) {
                const [start, end] = eventData.range.x;
                setStart(start);
                setEnd(end);
                setSelectedRange({ start, end });
              }
            }}
          />
        </div>

        {/* Selected Range Display */}
        {selectedRange && (
          <div className="text-sm text-gray-600">
            Selected Range: {selectedRange.start.toFixed(2)}s - {selectedRange.end.toFixed(2)}s
            ({(selectedRange.end - selectedRange.start).toFixed(2)}s)
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-between pt-4">
          <button
            type="button"
            onClick={() => goToLevel('epoch')}
            className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Create Annotation
          </button>
        </div>
      </form>
    </div>
  );
};

export default NewAnnotation; 